"use client";

import type { MetricCell } from "@/lib/conditions/metric-strip";
import type { OfficialStatusLink as OfficialStatusLinkData } from "@/lib/conditions/ski/official-status-links";
import { setShallowSearchParams } from "@/lib/hooks/use-shallow-search-param";
import { sportLabel } from "@/lib/conditions/sport-meta";
import { MetricStrip } from "@/app/_components/conditions/MetricStrip";
import { OfficialStatusLink } from "./OfficialStatusLink";
import type { SkiStatusView } from "./ski-v3-view";

/**
 * Headline for a ski sport whose score is withheld: "Closed" (or "No lift access" for
 * off-piste at a closed resort) and never a number. Off-piste without lift access points to
 * touring, which needs no lifts and keeps its natural-snow score.
 */
export function SkiStatusHeadline({
  view,
  metrics = [],
  touringAvailable = false,
  officialLink = null,
}: {
  view: SkiStatusView;
  metrics?: MetricCell[];
  touringAvailable?: boolean;
  officialLink?: OfficialStatusLinkData | null;
}) {
  return (
    <section className="relative" data-ski-status={view.label === "Closed" ? "closed" : "no-lift-access"}>
      <div className="bg-white rounded-[16px] border border-border-light p-5 md:p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
        <div className="text-[11px] font-bold uppercase tracking-[1px] text-gold">
          {sportLabel(view.sport)} · Today’s status
        </div>
        <p className="mt-0.5 font-serif text-[36px] md:text-[40px] font-bold leading-none text-navy">{view.label}</p>
        <p className="mt-2 text-[14px] leading-[1.5] text-text-body">{view.reason}</p>
        {view.pointToTouring && (
          <div className="mt-3 rounded-[10px] border-l-[3px] border-gold bg-cream px-3 py-2 text-[13px] leading-[1.5] text-navy">
            <p>Touring needs no lifts, so it still has a natural-snow score today.</p>
            {touringAvailable && (
              <button
                type="button"
                onClick={() => setShallowSearchParams({ sport: "touring" })}
                className="mt-1.5 inline-flex min-h-11 items-center rounded-full bg-navy px-4 text-[12.5px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2"
              >
                See the touring score
              </button>
            )}
          </div>
        )}
        <MetricStrip cells={metrics} />
        {officialLink && (
          <div className="mt-2">
            <OfficialStatusLink link={officialLink} />
          </div>
        )}
      </div>
    </section>
  );
}
